import { NgModule, inject } from '@angular/core';
import { RouterModule, Routes, NoPreloading, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { AcceptCompanyInvitationComponent } from './modules/auth/pages/accept-company-invitation/accept-company-invitation.component';
import { AcceptInvitationComponent } from './modules/auth/pages/accept-invitation/accept-invitation.component';
import { ChangePasswordComponent } from './modules/auth/pages/change-password/change-password.component';
import { EmailConfirmationComponent } from './modules/auth/pages/email-confirmation/email-confirmation.component';
import { ForbiddenComponent } from './modules/auth/pages/forbidden/forbidden.component';
import { ForgotPasswordComponent } from './modules/auth/pages/forgot-password/forgot-password.component';
import { LoginComponent } from './modules/auth/pages/login/login.component';
import { DemoLandingComponent } from './modules/auth/pages/demo-landing/demo-landing.component';
import { demoEnabledGuard } from './modules/auth/services/guard/demo-enabled.guard';
import { leaveToLiveRegistration } from './modules/auth/services/demo/registration-target';
import { RegisterUserComponent } from './modules/auth/pages/register-user/register-user.component';
import { RegisteredStepperComponent } from './onboarding/component/registered-stepper/registered-stepper.component';
import { ResetPasswordComponent } from './modules/auth/pages/reset-password/reset-password.component';
import { AuthGuard } from './modules/auth/services/guard/auth.guard';
import { PermissionGuard } from './modules/employee/guards/permission.guard';
import { PaymentConfirmationComponent } from './modules/feature-toggle/component/payment-confirmation/payment-confirmation.component';
import { FeatureGuard } from './modules/feature-toggle/guards/feature.guard';
import { Feature } from './modules/feature-toggle/enums/features.enum';
import { PaymentSuccessComponent } from './modules/feature-toggle/component/payment-success/payment-success.component';
import { environment } from 'src/environments/environment';
import { TasksModuleGuard } from './modules/tasks/gate/tasks-module.guard';
import { notificationDeepLinkGuard } from './navigation/notification-deep-link/notification-deep-link.guard';
import { NOTIFICATION_DEEP_LINK_DATA_KEY } from './navigation/notification-deep-link/notification-deep-link';

/**
 * Kurzpfade aus Glocke und Push (P4, 17.10.2026): rollenneutral vom Server, rendern nie etwas —
 * `notificationDeepLinkGuard` leitet je Rolle und Gerät auf die echte Seite weiter
 * (Tabelle in `navigation/notification-deep-link/notification-deep-link.ts`). `AuthGuard` zuerst:
 * abgemeldet geht es mit `returnUrl` zur Anmeldung und danach wieder hierher.
 */
const notificationDeepLinkRoute = (path: string, kind: string) => ({
  path,
  canActivate: [AuthGuard, notificationDeepLinkGuard],
  data: { [NOTIFICATION_DEEP_LINK_DATA_KEY]: kind },
  children: [],
});

// Payment Success Component (neu erstellen oder importieren)

// Guard: Block /registration on iOS (App Store Guideline 3.1.1)
const notIosGuard = () => {
  if (Capacitor.getPlatform() === 'ios') {
    return inject(Router).createUrlTree(['/login']);
  }
  return true;
};

/**
 * Guard: Auf der Demo-Instanz gibt es KEINE eigene Registrierung — sie fuehrt hinueber
 * in die Live-App.
 *
 * Eine hier abgeschickte Registrierung legt die Firma in der **Demo-Datenbank** an. Sie
 * taucht danach in keiner Kundenliste auf, wird nicht als Conversion gemeldet und faellt
 * irgendwann dem Demo-Cleanup zu. Am 17.09.2026 ist das einem echten Interessenten
 * passiert, der ueber eine bezahlte Anzeige kam (Begruendung: {@link liveRegistrationUrl}).
 *
 * Der Guard steht bewusst an der ROUTE und nicht nur an den Knoepfen: Er deckt auch die
 * getippte Adresse, einen alten Lesezeichen-Link und jede kuenftige CTA ab, die die
 * Weiche vergisst. Im Live-/Test-/Lokal-Build ist `liveAppUrl` leer — dort passiert
 * nichts.
 */
const liveRegistrationGuard = () => !leaveToLiveRegistration('demo');

export const routes: Routes = [
  {
    // Umgebungsabhängiger Einstieg: NUR auf der dedizierten Demo-Instanz (`demoIsHome`, z. B.
    // demo.fixplaner.com) landen NICHT-eingeloggte Besucher direkt auf der `/demo`-Willkommens-Seite.
    // Lokal/Test/Prod bleibt die Root auf `/login` — dort ist `/demo` nur per Direkt-URL erreichbar
    // (zum Testen, sofern `demoEnabled`). Eingeloggte gehen über `/login`, das rollenabhängig weiterleitet.
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const isLoggedIn = !!localStorage.getItem('token');
      return !isLoggedIn && environment.demoIsHome === true ? '/demo' : '/login';
    },
  },
  // DEV-/Test-only: Lebender Design-System-Komponenten-Katalog (echte xds-Komponenten).
  // Der Lazy-Loader lebt im Environment (environment.ts / environment.test.ts). In prod/demo
  // ist `environment.catalog` === null → diese Route wird gar nicht registriert UND die
  // import()-Expression existiert im Build-Graph nicht mehr, wodurch der /_catalog-Chunk
  // NICHT ins Prod-Bundle gelangt (echter Ausschluss statt nur Routing-Guard).
  ...(environment.catalog
    ? [{ path: '_catalog', loadChildren: environment.catalog }]
    : []),
  { path: 'login', component: LoginComponent },
  // Öffentliche Self-Service-Demo-Willkommens-Seite. Nur im Demo-Build erreichbar (demoEnabledGuard);
  // notIosGuard, da die Demo bewusst Web/PWA-only ist (App-Store-Richtlinien wie /registration).
  { path: 'demo', component: DemoLandingComponent, canActivate: [demoEnabledGuard, notIosGuard] },
  { path: 'forgotpassword', component: ForgotPasswordComponent },
  {
    path: 'registration',
    component: RegisterUserComponent,
    canActivate: [liveRegistrationGuard, notIosGuard],
  },
  { path: 'forbidden', component: ForbiddenComponent },
  { path: 'email-confirmation', component: EmailConfirmationComponent },
  { path: 'accept-invitation', component: AcceptInvitationComponent },
  { path: 'accept-company-invitation', component: AcceptCompanyInvitationComponent },
  { path: 'resetpassword', component: ResetPasswordComponent },
  { path: 'reset-password', redirectTo: 'resetpassword', pathMatch: 'full' },
  { path: 'change-password', component: ChangePasswordComponent },
  {
    path: 'registered-stepper',
    component: RegisteredStepperComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'employeedashboard',
    redirectTo: 'employee/dashboard',
    pathMatch: 'full'
  },
  notificationDeepLinkRoute('absences/open/:id', 'absence'),
  notificationDeepLinkRoute('time/corrections/open/:id', 'correction'),
  notificationDeepLinkRoute('shifts/swaps/open/:id', 'swap'),
  notificationDeepLinkRoute('shifts/week/:isoWeek', 'week'),
  notificationDeepLinkRoute('shifts/open-shifts', 'openShifts'),
  notificationDeepLinkRoute('absences/review/:id', 'absenceReview'),
  notificationDeepLinkRoute('time/corrections/review/:id', 'correctionReview'),
  notificationDeepLinkRoute('employees/change-requests/open/:id', 'changeRequest'),
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./modules/dashboard/dashboard.module').then(
        (m) => m.DashboardModule
      ),
    canActivate: [AuthGuard],
    data: { permittedRoles: ['MANAGER', 'TEAMLEADER'] },
  },
  {
    // Redirect alte Route zur neuen Dashboard-Route
    path: 'admin-dashboard',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: 'planner',
    loadChildren: () =>
      import('./modules/planner/planner.module').then(
        (m) => m.PlannerModule
      ),
    canActivate: [AuthGuard, FeatureGuard],
    data: {
      permittedRoles: ['MANAGER', 'TEAMLEADER'],
      features: [Feature.SHIFT_PLANNING_BASIC],
      requireAll: true
    },
  },
  {
    // Verfügbarkeits-Kalender des Managers.
    //
    // ⚠️ **Kein Feature-Gate — mit Absicht.** Bis zum 01.09.2026 stand hier
    // `features: [Feature.SHIFT_SWAP_ADVANCED]`. Dieses Add-on liegt laut
    // `SubscriptionSeeder.cs:675,723` nur in Plus und Pro; Starter- und
    // Spark-Kunden erreichten die Ansicht also gar nicht — obwohl sie fertig
    // ist. Und weil der Mitarbeiter-Tab `tab-availability`
    // (`employee-mobile-tabs.router.module.ts`) **nie** gegated war, trugen
    // deren Mitarbeiter Verfügbarkeiten ein, die in derselben Firma niemand
    // ansehen konnte. Dazu kam: „Erweiterter Schichttausch" beschreibt
    // automatisches Matching und hat mit Verfügbarkeit fachlich nichts zu tun.
    //
    // Auch `FeatureGuard` ist raus, nicht nur `features`: Bei leerer
    // Feature-Liste steigt er über `featuresLoaded$.pipe(map(() => true))` aus
    // (`feature.guard.ts:39-41`) — die Route würde weiter auf das Laden der
    // Features warten, ohne sie zu brauchen.
    //
    // Wer Verfügbarkeit künftig verkaufen will, legt ein **eigenes** Feature an
    // (Katalog + Seed + Plan-Zuordnung) und gated dann auch den Mitarbeiter-Tab
    // — sonst entsteht derselbe Widerspruch neu.
    path: 'availability',
    loadChildren: () =>
      import('./modules/availability/availability.module').then(
        (m) => m.AvailabilityModule
      ),
    canActivate: [AuthGuard],
    data: {
      permittedRoles: ['MANAGER', 'TEAMLEADER'],
    },
  },
  // Die Route `shift-swap-approvals` ist am 14.09.2026 entfallen. Dieselbe Liste war ueber
  // eine Vollseite hier UND ein Pop-up im Planer erreichbar; geblieben ist das Pop-up, weil
  // der Manager Tausche dort entscheidet, wo er den Dienstplan sieht. Die Rechtepruefung ist
  // nicht verloren gegangen, sondern sitzt weiterhin in `swap-shift/gate.ts` — sie entscheidet
  // jetzt ueber den Planer-Knopf statt ueber den Routenzugang (Rolle MANAGER/ADMIN ohne
  // TEAMLEADER, Abo SHIFT_SWAP_BASIC, Firmeneinstellung AllowShiftSwap,
  // `Shift.CanApproveShiftSwaps`).
  {
    path: 'settings',
    loadChildren: () =>
      import('./modules/settings/settings.module').then(
        (m) => m.SettingsModule
      ),
    canActivate: [AuthGuard],
    data: { permittedRoles: ['MANAGER', 'TEAMLEADER'] },
  },
  {
    path: 'employee',
    loadChildren: () =>
      import('./modules/employee/employee.module').then(
        (m) => m.EmployeeModule
      ),
    canActivate: [AuthGuard]
  },
  {
    path: 'resource-plan',
    redirectTo: '/staffing-management',
    pathMatch: 'full',
  },
  {
    path: 'absence',
    loadChildren: () =>
      import('./modules/absence/absence.module').then(
        (m) => m.AbsenceModule
      ),
    canActivate: [AuthGuard, FeatureGuard],
    data: {
      permittedRoles: ['MANAGER', 'TEAMLEADER'],
      features: [Feature.ABSENCE_MANAGEMENT_BASIC],
      requireAll: true,
    },
  },
  {
    path: 'time-recording',
    loadChildren: () =>
      import('./modules/time-recording/time-recording.module').then(
        (m) => m.TimeRecordingModule
      ),
    canActivate: [AuthGuard, FeatureGuard],
    data: {
      permittedRoles: ['MANAGER', 'TEAMLEADER'],
      features: [Feature.TIME_TRACKING_BASIC],
      requireAll: true,
    },
  },
  {
    // Aufgaben-Feature (06-ENTRYPOINT-NAV.md). Bewusst OHNE permittedRoles-
    // Einschränkung: auch Employees landen hier (S3 „Meine Aufgaben"). Das
    // 3-Zustands-Gate (Nav) + serverseitige Rechte/Scope regeln die Sichtbarkeit;
    // FeatureGuard erzwingt feature.tasks (Seed-Id 64) auch bei Direkt-URL.
    path: 'tasks',
    loadChildren: () =>
      import('./modules/tasks/tasks.module').then((m) => m.TasksModule),
    // TasksModuleGuard: Firmen-Schalter „Aufgaben aktiv" / „Mitarbeiterzugang".
    canActivate: [AuthGuard, FeatureGuard, TasksModuleGuard],
    data: {
      features: [Feature.TASK_MANAGEMENT],
      requireAll: true,
    },
  },
  {
    path: 'payment-confirmation',
    component: PaymentConfirmationComponent,
    canActivate: [AuthGuard],
    data: { permittedRoles: ['MANAGER', 'TEAMLEADER'] },
  },
  {
    path: 'payment-success',
    component: PaymentSuccessComponent, // Neue ungeschützte Route für externe Redirects
  },
  {
    // `/terminal-pairing` allein traf im TerminalModule den Kindpfad '' — das DASHBOARD, hier
    // ohne AuthGuard und FeatureGuard. Bis zum 23.09.2026 war das ein zweiter, ungeschützter
    // Eingang zum Terminal.
    path: 'terminal-pairing',
    pathMatch: 'full',
    redirectTo: 'terminal-pairing/pairing',
  },
  {
    path: 'terminal-pairing',
    loadChildren: () =>
      import('./modules/terminal/terminal.module').then(
        (m) => m.TerminalModule
      ),
    // NO AuthGuard - Öffentlich zugänglich für QR-Code Pairing!
    // QR-Code URL: /terminal-pairing/pairing
  },
  {
    path: 'terminal-dashboard',
    loadChildren: () =>
      import('./modules/terminal/terminal.module').then(
        (m) => m.TerminalModule
      ),
    canActivate: [AuthGuard, FeatureGuard],
    data: {
      permittedRoles: ['TERMINAL'],
      features: [Feature.TIME_TRACKING_TERMINAL],
      requireAll: true,
    },
    // Route führt zu: TerminalDashboardComponent via 'dashboard' child route
  },
  {
    path: 'employee-mobile',
    loadChildren: () =>
      import(
        './modules/employee/mobile-pages/employee-mobile-tabs/employee-mobile-tabs.module'
      ).then((m) => m.EmployeeMobileTabsPageModule),
    canActivate: [AuthGuard],
    data: { permittedRoles: ['EMPLOYEE', 'MANAGER', 'TEAMLEADER'] },
  },
  {
    path: 'profile',
    loadChildren: () =>
      import(
        './modules/employee/mobile-pages/profile-management/employee-profile.module'
      ).then((m) => m.EmployeeProfileModule),
  },
  {
    path: 'user-settings',
    loadChildren: () =>
      import('./modules/settings/user-settings/user-settings.module').then(
        (m) => m.UserSettingsPageModule
      ),
    canActivate: [AuthGuard],
  },
  {
    // E1 (Planning-Redesign, Redline 1 + D-12): Die Seite „Personalbedarf"
    // ist im ▦-Muster-Modus der fusionierten Planung aufgegangen. Externe
    // Bookmarks landen hier — Redirect behält ?date= (Woche) bei und öffnet
    // direkt den ▦-Blickwinkel. (Erklär-Banner verworfen — nur Testkunden,
    // keine Bestandskunden-Migration nötig.)
    // Permission-/Feature-Gates: /planner prüft MANAGER/TEAMLEADER +
    // SHIFT_PLANNING_BASIC; Staffing-Aktionen gaten in den Komponenten (D-14).
    path: 'staffing-management',
    redirectTo: (redirectData) => {
      const router = inject(Router);
      return router.createUrlTree(['/planner'], {
        queryParams: {
          ...redirectData.queryParams,
          view: 'muster',
        },
      });
    },
  },
  {
    path: 'organization',
    loadChildren: () =>
      import('./modules/organization/organization.module').then(
        (m) => m.OrganizationModule
      ),
    canActivate: [AuthGuard],
    data: { permittedRoles: ['MANAGER', 'TEAMLEADER'] },
  },
  {
    path: 'capacity-management',
    loadChildren: () =>
      import('./modules/capacity-management/capacity-management.module').then(
        (m) => m.CapacityManagementModule
      ),
    canActivate: [AuthGuard, FeatureGuard],
    data: {
      permittedRoles: ['MANAGER', 'TEAMLEADER'],
      features: [Feature.CAPACITY_MANAGEMENT],
      requireAll: true,
    },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // Changed from PreloadAllModules to NoPreloading for faster initial load
      // Lazy-loaded modules will only be loaded when navigated to
      preloadingStrategy: NoPreloading
    })
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
